import { useEffect, useState } from "react";
import { Globe2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const Row = ({ row, testId }) => (
  <li className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={testId}>
    <span className="w-8 shrink-0 font-mono uppercase text-brand">{row.code}</span>
    <span className="w-36 shrink-0 truncate text-ink">{row.name}</span>
    <span className="shrink-0 tabular-nums text-ink-soft">
      {[row.asked && `${row.asked} asked`,
        row.members && `${row.members} members`,
        row.plays && `${row.plays} tour plays`,
        row.translations && `${row.translations} translations`,
        row.phrases && `${row.phrases} phrases`]
        .filter(Boolean).join(" · ") || "no interest yet"}
    </span>
    {row.gaps.length > 0 && (
      <span className="flex flex-wrap gap-1">
        {row.gaps.map((gap) => (
          <span key={gap}
                className="rounded-full border border-[#F5C451]/30 bg-[#F5C451]/10 px-2 py-0.5 text-[10px] text-[#F5C451]">
            no {gap}
          </span>
        ))}
      </span>
    )}
  </li>
);

// Which languages people arrive wanting, and where ChatMiLu is still thin in them.
export const LanguageDemand = () => {
  const [demand, setDemand] = useState(null);

  useEffect(() => {
    api.get("/admin/langs/demand").then(({ data }) => setDemand(data))
      .catch((e) => console.error("could not load the language demand", e));
  }, []);

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="language-demand">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
        <Globe2 className="h-4 w-4 text-brand" /> What languages people want
      </p>

      {!demand ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-ink-soft" />
      ) : (
        <>
          <p className="mt-3 text-xs text-ink-soft" data-testid="language-demand-signals">
            {demand.offered} languages offered · {demand.covered_well.length} written all the way
            through ({demand.pieces.join(", ")}) · {demand.signals.asked} visitors' browsers counted
          </p>

          <p className="mt-5 text-xs font-semibold text-ink">Asked for, not offered at all</p>
          {demand.not_offered.length === 0 ? (
            <p className="mt-2 text-[11px] text-ink-soft" data-testid="language-demand-none-outside">
              Nobody has turned up wanting a language outside the fifty yet.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {demand.not_offered.map((row) => (
                <Row key={row.code} row={row} testId={`lang-missing-${row.code}`} />
              ))}
            </ul>
          )}

          <p className="mt-6 text-xs font-semibold text-ink">Offered, but thin</p>
          {demand.thin.length === 0 ? (
            <p className="mt-2 text-[11px] text-ink-soft" data-testid="language-demand-none-thin">
              Nobody is reading in a language that is still missing pieces.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {demand.thin.map((row) => (
                <Row key={row.code} row={row} testId={`lang-thin-${row.code}`} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
